import React, { useEffect, useState, useRef } from 'react';
import { CheckCircle2, Lock, Zap, ShieldCheck, CreditCard } from 'lucide-react';
type PlanType = 'sic' | 'packfull';
export function PricingSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PlanType>('packfull');
  const sectionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => observer.disconnect();
  }, []);
  const isPackFull = selectedPlan === 'packfull';
  const ctaLink = isPackFull ? 'https://checkout.rincondigital.shop/cart/52917897363731:1' : 'https://checkout.rincondigital.shop/cart/52917830517011:1';
  return <section id="purchase" ref={sectionRef} className="py-16 bg-slate-50 relative overflow-hidden" data-id="element-162">
      <div className="max-w-3xl mx-auto px-4 relative z-10" data-id="element-163">
        {/* Section Header */}
        <div className="text-center mb-10" data-id="element-164">
          <h2 className={`text-4xl md:text-5xl font-black mb-4 text-slate-900 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`} data-id="element-165">
            Elegí tu plan
          </h2>
          <p className={`text-xl text-slate-600 transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`} data-id="element-166">
            Todo lo que necesitás para enseñar sin estrés
          </p>
        </div>

        {/* Main Pricing Card (Dark) */}
        <div className={`bg-slate-900 rounded-3xl shadow-2xl overflow-hidden border border-slate-800 p-6 md:p-10 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-167">
          <div className="space-y-4 mb-8" data-id="element-168">
            {/* Option 1: SIC 360 */}
            <label className={`relative flex flex-col sm:flex-row items-start sm:items-center justify-between p-5 md:p-6 rounded-2xl cursor-pointer transition-all duration-300 border-2 ${!isPackFull ? 'bg-slate-800 border-blue-500' : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'}`} data-id="element-169">
              <div className="flex items-center gap-4 mb-4 sm:mb-0" data-id="element-170">
                {/* Radio Circle */}
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${!isPackFull ? 'border-blue-500' : 'border-slate-500'}`} data-id="element-171">
                  {!isPackFull && <div className="w-3 h-3 rounded-full bg-blue-500" data-id="element-172" />}
                </div>

                <div data-id="element-173">
                  <div className="flex items-center gap-2 mb-1" data-id="element-174">
                    <span className="text-xl" data-id="element-175">📘</span>
                    <h3 className="text-xl font-black text-white" data-id="element-176">SIC 360</h3>
                  </div>
                  <p className="text-slate-400 text-sm" data-id="element-177">
                    Clases anuales resueltas + 4 bonos
                  </p>
                </div>
              </div>

              <div className="text-left sm:text-right ml-10 sm:ml-0" data-id="element-178">
                <p className="text-slate-500 line-through text-sm font-semibold mb-0.5" data-id="element-179">
                  $49.990
                </p>
                <p className="text-3xl font-black text-white" data-id="element-180">$19.990</p>
              </div>

              {/* Hidden radio input for accessibility */}
              <input type="radio" name="pricing-plan" value="sic" checked={!isPackFull} onChange={() => setSelectedPlan('sic')} className="sr-only" aria-label="Seleccionar plan SIC 360" data-id="element-181" />
            </label>

            {/* Option 2: PACK FULL 360 */}
            <label className={`relative flex flex-col sm:flex-row items-start sm:items-center justify-between p-5 md:p-6 rounded-2xl cursor-pointer transition-all duration-300 border-2 ${isPackFull ? 'bg-slate-800 border-orange-500 shadow-[0_0_20px_rgba(249,115,22,0.15)]' : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'}`} data-id="element-182">
              <div className="flex items-start gap-4 mb-4 sm:mb-0" data-id="element-183">
                {/* Radio Circle */}
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-colors ${isPackFull ? 'border-orange-500' : 'border-slate-500'}`} data-id="element-184">
                  {isPackFull && <div className="w-3 h-3 rounded-full bg-orange-500" data-id="element-185" />}
                </div>

                <div data-id="element-186">
                  <div className="flex flex-wrap items-center gap-2 mb-2" data-id="element-187">
                    <span className="text-xl" data-id="element-188">🏆</span>
                    <h3 className="text-xl font-black text-white" data-id="element-189">
                      PACK FULL 360
                    </h3>
                  </div>

                  {/* Badges */}
                  <div className="flex flex-wrap gap-2 mb-2" data-id="element-190">
                    <span className="bg-orange-500 text-white text-xs font-bold px-2.5 py-1 rounded-md flex items-center gap-1" data-id="element-191">
                      ⭐ MÁS ELEGIDO
                    </span>
                    <span className="bg-green-600/20 text-green-400 border border-green-600/30 text-xs font-bold px-2.5 py-1 rounded-md" data-id="element-192">
                      AHORRÁS $30.000
                    </span>
                  </div>

                  <p className="text-slate-400 text-sm max-w-xs" data-id="element-193">
                    SIC 360 + Micro y Macro economía 360 + 100 Emprendimientos +
                    8 bonos
                  </p>
                </div>
              </div>

              <div className="text-left sm:text-right ml-10 sm:ml-0 self-start sm:self-center" data-id="element-194">
                <p className="text-slate-500 line-through text-sm font-semibold mb-0.5" data-id="element-195">
                  $69.990
                </p>
                <p className="text-3xl font-black text-orange-400" data-id="element-196">$39.990</p>
              </div>

              {/* Hidden radio input for accessibility */}
              <input type="radio" name="pricing-plan" value="packfull" checked={isPackFull} onChange={() => setSelectedPlan('packfull')} className="sr-only" aria-label="Seleccionar plan PACK FULL 360" data-id="element-197" />
            </label>
          </div>

          {/* Pack Full Details Module (Conditional) */}
          <div className={`transition-all duration-500 overflow-hidden ${isPackFull ? 'max-h-96 opacity-100 mb-8' : 'max-h-0 opacity-0 mb-0'}`} data-id="element-198">
            <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700" data-id="element-199">
              <p className="text-slate-300 text-sm font-bold mb-4 uppercase tracking-wider" data-id="element-200">
                ¿Qué incluye el Pack Full?
              </p>
              <ul className="space-y-3" data-id="element-201">
                <li className="flex items-start gap-3" data-id="element-202">
                  <span className="text-lg leading-none" data-id="element-203">📘</span>
                  <p className="text-slate-300 text-sm" data-id="element-204">
                    <span className="font-bold text-white" data-id="element-205">SIC 360:</span>{' '}
                    Clases anuales resueltas de SIC I y SIC II
                  </p>
                </li>
                <li className="flex items-start gap-3" data-id="element-206">
                  <span className="text-lg leading-none" data-id="element-207">📗</span>
                  <p className="text-slate-300 text-sm" data-id="element-208">
                    <span className="font-bold text-white" data-id="element-209">
                      Micro y Macro 360:
                    </span>{' '}
                    Economía completa para todo el año
                  </p>
                </li>
                <li className="flex items-start gap-3" data-id="element-210">
                  <span className="text-lg leading-none" data-id="element-211">💡</span>
                  <p className="text-slate-300 text-sm" data-id="element-212">
                    <span className="font-bold text-white" data-id="element-213">
                      100 Emprendimientos:
                    </span>{' '}
                    Ideas reales para trabajar con alumnos de último año
                  </p>
                </li>
              </ul>
            </div>
          </div>

          {/* CTA Button */}
          <div className="mb-8" data-id="element-214">
            <a href={ctaLink} className={`block w-full font-black py-5 px-8 rounded-xl shadow-lg transform transition-all hover:scale-[1.02] text-center text-xl ${isPackFull ? 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`} data-id="element-215">
              {isPackFull ? 'SÍ, QUIERO PACK COMPLETO →' : 'QUIERO SIC 360 →'}
            </a>

            <div className={`transition-all duration-300 overflow-hidden ${isPackFull ? 'max-h-10 opacity-100 mt-4' : 'max-h-0 opacity-0 mt-0'}`} data-id="element-216">
              <p className="text-center text-slate-400 text-sm italic" data-id="element-217">
                La opción más elegida por docentes que enseñan en los dos
                niveles
              </p>
            </div>
          </div>

          {/* Trust Badges */}
          <div className="pt-6 border-t border-slate-800" data-id="element-218">
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-4 text-slate-400 text-sm font-medium" data-id="element-219">
              <div className="flex items-center gap-2" data-id="element-220">
                <Lock className="w-4 h-4 text-slate-500" data-id="element-221" />
                <span data-id="element-222">Pago seguro</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-223">
                <Zap className="w-4 h-4 text-yellow-500" data-id="element-224" />
                <span data-id="element-225">Acceso en 5 min</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-226">
                <ShieldCheck className="w-4 h-4 text-slate-500" data-id="element-227" />
                <span data-id="element-228">Garantía 7 días</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-229">
                <CreditCard className="w-4 h-4 text-slate-500" data-id="element-230" />
                <span data-id="element-231">Mercado Pago</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}